/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { AwvInfo, AwvInfoConditionEnum, ResultJobList } from "client";
import { useUserContext } from "context/UserContext";
import { ConfirmDialog } from "components/Common/ConfirmDialog";
import { BorderedButton } from "components/Common/BorderedButton";
import { BorderedIconButton } from "components/Common/BorderedIconButton";
import { CameraIcon } from "components/Common/CameraIcon";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { TaskRegisterMultipleChargeDialog } from "components/TaskRegisterMultipleCharge/TaskRegisterMultipleChargeDialog";

interface Props {
  awvInfo: AwvInfo;
  tasks?: Array<ResultJobList>;
  cancelExecute: (task: ResultJobList) => void;
  deleteTaskExecute: (jobsetId: string) => void;
}

export const AwvControl: FC<Props> = ({
  awvInfo,
  tasks,
  cancelExecute,
  deleteTaskExecute,
}) => {
  const { t } = useTranslation();
  const { roleName } = useUserContext();
  const navigate = useNavigate();
  const [bgColor, setBgColor] = useState<string>("white");
  const [openMultipleCharge, setOpenMultipleCharge] = useState(false);

  let jobsetId: string | undefined = undefined;
  if (tasks && tasks.length > 0) {
    jobsetId = tasks[0].jobSetId;
  }

  const canCancel =
    roleName !== "awv_operator" &&
    !!awvInfo.condition &&
    awvInfo.condition !== AwvInfoConditionEnum.Offline &&
    (tasks?.some((e) => e.taskStatus === 2) ?? false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleCancel = () => {
    setConfirmOpen(true);
  };

  const canEdit =
    roleName !== "awv_operator" &&
    !!awvInfo.condition;
  const handleEdit = () => {
    setOpenMultipleCharge(true);
  };

  const canDelete =
    roleName !== "awv_operator" &&
    !!awvInfo.condition && !!jobsetId;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleCloseTaskRegisterMultipleCharge = useCallback(() => {
    setOpenMultipleCharge(false);
  }, []);

  const handleCamera = () => {
    navigate("/camera", { state: awvInfo });
  };

  useEffect(() => {
    if (
      !awvInfo.condition ||
      awvInfo.condition === AwvInfoConditionEnum.Offline
    ) {
      setBgColor("#E6E6E6");
    } else if (awvInfo.condition === AwvInfoConditionEnum.Error) {
      setBgColor("#FFD9D9");
    } else {
      setBgColor("white");
    }
  }, [awvInfo.condition]);

  const [isError, setIsError] = useState(false);

  return (
    <CustomContainer bg={bgColor}>
      <BorderedButton disabled={!canCancel} onClick={handleCancel}>
        {t("AWVUI-MSG29")}
      </BorderedButton>
      <BorderedButton disabled={!canEdit} onClick={handleEdit}>
        {t("AWVUI-MSG189")}
      </BorderedButton>
      <BorderedButton disabled={!canDelete} onClick={handleDelete}>
        {t("AWVUI-MSG222")}
      </BorderedButton>
      <BorderedIconButton
        disabled={
          !awvInfo.condition ||
          awvInfo.condition === AwvInfoConditionEnum.Offline
        }
        onClick={handleCamera}
      >
        <CameraIcon />
      </BorderedIconButton>
      <ConfirmDialog
        open={confirmOpen}
        message={t("AWVUI-MSG08")}
        negativeButtonText={t("AWVUI-MSG12")}
        onNegativeButtonClick={() => {
          setConfirmOpen(false);
        }}
        positiveButtonText={t("AWVUI-MSG11")}
        onPositiveButtonClick={() => {
          setConfirmOpen(false);
          // find running task.
          const cancelTarget = tasks?.find((task) => task.taskStatus === 2);
          if (cancelTarget) {
            cancelExecute(cancelTarget);
          }
        }}
      />
      {jobsetId && (
        <ConfirmDialog
          open={deleteConfirmOpen}
          message={t("AWVUI-MSG09")}
          negativeButtonText={t("AWVUI-MSG12")}
          onNegativeButtonClick={() => {
            setDeleteConfirmOpen(false);
          }}
          positiveButtonText={t("AWVUI-MSG11")}
          onPositiveButtonClick={() => {
            deleteTaskExecute(jobsetId!);
            setDeleteConfirmOpen(false);
          }}
        />
      )}
      <ErrorDialog
        open={isError}
        onPositiveButtonClick={() => {
          setIsError(false);
        }}
        message={t("AWVUI-MSG177")}
        positiveButtonText={t("AWVUI-MSG22")}
      />
      {openMultipleCharge && (
        <TaskRegisterMultipleChargeDialog
        open={openMultipleCharge}
        jobsetId={jobsetId}
        onClose={handleCloseTaskRegisterMultipleCharge}
        />
      )}
    </CustomContainer>
  );
};

interface CustomContainerProps {
  bg: string;
}

const CustomContainer = styled(Box)<CustomContainerProps>(({ theme, bg }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.66, 1),
  backgroundColor: bg,
}));
